import { participantColor, type ConnectionQuality, type IRemoteControlState } from '@srm/screen-sharing/light';
import { joinNames } from '@srm/ui/lib/chatText';
import type { IScreenShareConnectionState, IScreenShareParticipant, ScreenShareTransport } from '@srm/screen-sharing';
import type { ISupportBarPerson, SupportBarTone } from '@molecules/SupportBar';
import type { SupportFrameTone } from '@atoms/SupportFrame';
import type { ISupportVoiceState } from '@providers/ScreenShareProvider/supportVoice';

export type ScreenShareBarKind =
    'waiting' | 'viewing' | 'requested' | 'control' | 'reconnecting' | 'peer-reconnecting' | 'suspended' | 'error';

export interface IScreenShareBarState {
    kind: ScreenShareBarKind;
    tone: SupportBarTone;
    title: string;
    /** Короче `title`, когда плашке не хватает места: «Козодоев Г. П. и ещё 1» (полный список — в подсказке стопки) */
    shortTitle?: string;
    description?: string;
    /** Подсказка к тексту полосы: что видно сотруднику и как прекратить (§40) */
    hint?: string;
    /** Сотрудник — инициалы в полосе */
    person?: string;
    /** Пользователь-сотрудник `person` — по наведению на кружок и имя открывается его карточка */
    personKey?: string;
    /** Сотрудники показа — кружки их цветов, управляющий выделен (IMPLEMENTATION.md §25) */
    people?: ISupportBarPerson[];
    busy?: boolean;
}

export interface IScreenShareSnapshot {
    active: boolean;
    error: string;
    status: string;
    control: IRemoteControlState;
    connection: IScreenShareConnectionState | null;
    viewerName: string | null;
    /** Все сотрудники показа; без них — один `viewerName` */
    participants?: IScreenShareParticipant[];
    /** Кто из сотрудников с микрофоном и кто говорит (§31) */
    voice?: Pick<ISupportVoiceState, 'active' | 'speaking'>;
    /** Показ приостановлен обновлением страницы и ждёт «Продолжить показ» (§35) */
    suspended?: boolean;
    /** «Продолжить показ» не получилось */
    resumeError?: string;
    /** Показ не начат: выбрали не вкладку (§40) */
    reselect?: boolean;
}

const SUPPORT = 'Сотрудник поддержки';

/**
 * Что видит сотрудник и как прекратить (§40) — подсказкой у текста полосы, пока экран показывается. Ошибки страницы и
 * запросов (§44.2) уходят сотруднику сами, пока идёт показ, — пакет диагностики (§43) только с согласия.
 */
export const SCREEN_SHARE_PRIVACY_TEXT =
    'Сотрудник видит только эту вкладку. Видео не записывается — сохраняются только снимки, которые сделает ' +
    'сотрудник, их видно в «Доступ поддержки». Пока идёт показ, сотрудник видит ошибки страницы и запросов (без ' +
    'значений полей), полный пакет технических сведений — только с вашего согласия. Прекратить показ — «Остановить показ».';
/** Запрос управления (§40): что сможет сотрудник и как забрать управление. */
export const SCREEN_SHARE_CONTROL_TEXT =
    'Сотрудник сможет нажимать и вводить текст в этой вкладке. Забрать управление в любой момент — Esc или ' +
    '«Забрать управление».';

/** «страница обновилась — продолжите в течение 2 мин»: столько показ ждёт на сервере (§35). */
const suspendedText = (graceSeconds: number | undefined) =>
    graceSeconds ? `страница обновилась — ${suspendedDeadline(graceSeconds)}` : 'страница обновилась';
/** «продолжите в течение 2 мин» — без срока сервера пусто. */
export const suspendedDeadline = (graceSeconds: number | undefined) =>
    graceSeconds ? `продолжите в течение ${Math.max(1, Math.round(graceSeconds / 60))} мин` : '';

/** Пользователь сотрудника по имени — для его карточки (у тёзок — первый). */
export const participantUserId = (
    participants: readonly Pick<IScreenShareParticipant, 'name' | 'userId'>[] | undefined,
    name: string | null | undefined,
): string | undefined => (name ? participants?.find((person) => person.name === name)?.userId : undefined);

const peopleOf = (share: IScreenShareSnapshot): ISupportBarPerson[] | undefined =>
    share.participants?.length
        ? share.participants.map((person) => ({
              name: person.name,
              color: participantColor(person.color),
              active: person.controlling,
              speakingKey: person.peerId,
              ...(person.userId ? { userKey: person.userId } : {}),
              ...(share.voice?.active.includes(person.peerId) ? { micOn: true } : {}),
              ...(share.voice?.speaking.includes(person.peerId) ? { speaking: true } : {}),
          }))
        : undefined;

/** Что показывает полоса поддержки (PLAN.md §7.1); `null` — полосы нет. */
export function resolveScreenShareBar(share: IScreenShareSnapshot): IScreenShareBarState | null {
    if (!share.active)
        return share.error
            ? {
                  kind: 'error',
                  tone: 'error',
                  title: share.reselect ? 'Показ экрана не начат' : 'Показ экрана остановлен',
                  description: share.error,
              }
            : null;
    // Обновление страницы (§35): захвата нет, показ ждёт нажатия — это главное, что нужно знать пользователю.
    if (share.suspended)
        return {
            kind: 'suspended',
            tone: 'neutral',
            title: 'Показ экрана приостановлен',
            description: share.resumeError || suspendedText(share.connection?.suspendGraceSeconds),
        };
    const name = share.control.peerName || share.viewerName || SUPPORT;
    const phase = share.connection?.phase;
    const people = peopleOf(share);
    const keyOf = (person: string) => {
        const userId = participantUserId(share.participants, person);
        return userId ? { personKey: userId } : {};
    };
    // Обрыв хаба управление не снимает (команды идут по каналу пары): «Забрать управление» и запрос остаются в полосе,
    // обрыв — в описании.
    const hubLost = phase === 'reconnecting' || phase === 'restoring';
    const lost = (text: string) => (hubLost ? `${text}, связь с сервером восстанавливается` : text);
    // Окно на сервере открыто, сотрудник ещё не подтвердил: ввод уже приостановлен, команды не выполняются.
    if (share.control.phase === 'granted' && share.control.allowing)
        return {
            kind: 'control',
            tone: 'control',
            busy: true,
            title: name,
            description: lost('открываем управление…'),
        };
    if (share.control.phase === 'granted')
        return {
            kind: 'control',
            tone: 'control',
            person: name,
            ...keyOf(name),
            ...(people ? { people } : {}),
            title: `Управляет ${name}`,
            description: lost('ваш ввод приостановлен'),
            hint: SCREEN_SHARE_CONTROL_TEXT,
        };
    if (share.control.phase === 'requested')
        return {
            kind: 'requested',
            tone: 'control',
            person: name,
            ...keyOf(name),
            title: name,
            description: lost('просит управление кабинетом'),
            hint: SCREEN_SHARE_CONTROL_TEXT,
        };
    if (hubLost)
        return {
            kind: 'reconnecting',
            tone: 'grey',
            busy: true,
            title: 'Связь прервалась',
            description: 'восстанавливаем, нажимать ничего не нужно',
        };
    if (phase === 'peer-reconnecting')
        return { kind: 'peer-reconnecting', tone: 'grey', busy: true, title: `${SUPPORT} переподключается…` };
    const names = share.participants?.length ? share.participants.map((person) => person.name) : [];
    if (names.length > 1)
        return {
            kind: 'viewing',
            tone: 'view',
            people,
            title: joinNames(names),
            shortTitle: `${names[0]} и ещё ${names.length - 1}`,
            description: 'смотрят ваш экран',
            hint: SCREEN_SHARE_PRIVACY_TEXT,
        };
    const viewer = names[0] || share.viewerName;
    if (viewer)
        return {
            kind: 'viewing',
            tone: 'view',
            person: viewer,
            ...keyOf(viewer),
            ...(people ? { people } : {}),
            title: viewer,
            description: share.control.pointer ? 'смотрит и показывает на экране' : 'смотрит ваш экран',
            hint: SCREEN_SHARE_PRIVACY_TEXT,
        };
    return {
        kind: 'waiting',
        tone: 'neutral',
        busy: true,
        title: 'Показ экрана включён',
        description: phase === 'live' ? 'ждём сотрудника поддержки' : share.status || 'подключаемся…',
        hint: SCREEN_SHARE_PRIVACY_TEXT,
    };
}

/** Рамка окна: синяя, пока экран смотрят, янтарная — на время управления. */
export function resolveScreenShareFrame(share: IScreenShareSnapshot): SupportFrameTone | null {
    if (!share.active || share.suspended) return null;
    if (share.control.phase === 'granted') return 'control';
    return share.viewerName || share.participants?.length || share.control.phase === 'requested' ? 'view' : null;
}

/**
 * Подпись точки качества в полосе (PLAN.md §7.1, «Слабый канал»). В режиме снимков (видеосвязь не установилась,
 * IMPLEMENTATION.md §20) — способ связи, а не качество видео.
 */
export const qualityText = (quality: ConnectionQuality, transport: ScreenShareTransport = 'webrtc') =>
    transport === 'snapshots'
        ? 'Связь через сервер (снимки)'
        : quality === 'good'
          ? 'Связь хорошая'
          : 'Слабый канал — качество снижено';

/** «Козодоев говорит», «Козодоев и Иванова говорят» — специалисты, чей голос слышен сейчас (§31). */
export function speakingText(
    participants: readonly Pick<IScreenShareParticipant, 'peerId' | 'name'>[] | undefined,
    speaking: readonly string[],
): string {
    const names = (participants ?? [])
        .filter((person) => speaking.includes(person.peerId))
        .map((person) => person.name.split(/\s+/)[0] || person.name);
    if (!names.length) return '';
    return `${joinNames(names)} ${names.length > 1 ? 'говорят' : 'говорит'}`;
}
